import PropTypes from 'prop-types';
import { Box, Input } from '@mui/material';
import { forwardRef, useState, useMemo, useCallback, useEffect } from 'react';
import { SearchIcon } from '../assets/icons/icons';
import { router, usePage } from '@inertiajs/react';
import debounce from 'lodash/debounce';
import { addQueryParam, removeQueryParam, getQueryParam } from '../utils/urlFunctions';
import VisuallyHiddenText from './VisuallyHiddenText';
import useAriaLiveStatus from '../hooks/useAriaLiveStatus';
import ScreenReaderOnly from './ScreenReaderOnly';

const SearchBar = forwardRef(
  ({ onActivate = () => void 0, isInModal, onSearchLoading = () => void 0 }, ref) => {
    const { url } = usePage();

    const [query, setQuery] = useState(getQueryParam(url, 'search') || '');
    // visually hidden search status message for screen readers
    const [ariaLiveStatus, setAriaLiveStatus] = useAriaLiveStatus();

    // Sync local query state with URL changes
    useEffect(() => {
      setQuery(getQueryParam(url, 'search') || '');
    }, [url]);

    const debouncedSearch = useMemo(
      () =>
        debounce((newQuery) => {
          let newUrl = removeQueryParam(url, 'search');
          if (newQuery.length > 1) {
            newUrl = addQueryParam(newUrl, 'search', newQuery);
          }
          const isSearchPage = new URL(location.href).pathname === '/search';
          router.visit(newUrl, {
            preserveScroll: true,
            preserveState: true,
            ...(isSearchPage ? {} : { only: ['search_results'] }),
            onSuccess: () => setAriaLiveStatus('Search results updated'),
            onError: () => setAriaLiveStatus('Search results failed to load'),
            onFinish: () => onSearchLoading?.(false),
          });
          onSearchLoading?.(true);
        }, 750),
      [url],
    );

    const [focused, setFocused] = useState(false);

    const handleInputChange = (e) => {
      onActivate();
      setAriaLiveStatus('Updating search results');
      const q = e.target.value;
      setQuery(q);
      debouncedSearch(q);
    };

    const handleFocus = () => {
      setFocused(true);
    };

    const handleBlur = () => {
      setFocused(false);
    };

    return (
      <>
        <ScreenReaderOnly>
          <label htmlFor="searchbar">Search products and resources</label>
        </ScreenReaderOnly>
        <Input
          id="searchbar"
          ref={ref}
          value={query}
          // We're not using onFocus here, because
          // we don't want to open the search modal
          // when a user enters the search bar via
          // the Tab key.
          onChange={handleInputChange}
          onClick={onActivate}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={focused ? null : 'Search products and resources'}
          disableUnderline
          data-testid="global-search"
          startAdornment={
            query || focused ? (
              <Box mr={1} display="flex" alignItems="center" justifyContent="center">
                Search:
              </Box>
            ) : (
              <Box mr={1} display="flex" alignItems="center" justifyContent="center">
                <SearchIcon />
              </Box>
            )
          }
          sx={{
            height: 46,
            width: isInModal ? '100%' : { xs: 100, md: 150, lg: 400 },
            backgroundColor: isInModal ? 'gray.100' : 'common.white',
            py: 1,
            pl: 2,
            pr: 1,
            borderRadius: 100,
            mb: isInModal ? 4 : 0,
            mt: isInModal ? 2 : 0,
          }}
          inputProps={{
            'aria-label': 'Search products and resources',
            sx: {
              p: 0,
            },
          }}
        />
        <VisuallyHiddenText aria-live="polite" role="status">
          {ariaLiveStatus}
        </VisuallyHiddenText>
      </>
    );
  },
);

SearchBar.propTypes = {
  onActivate: PropTypes.func,
  isInModal: PropTypes.bool,
  onSearchLoading: PropTypes.func,
};

// needed for arrow function declaration with forwardRef
// https://stackoverflow.com/questions/59716140/using-forwardref-with-proptypes-and-eslint
SearchBar.displayName = 'SearchBar';

export default SearchBar;
